import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '@/supabase'
import { useUserStore } from './user'
import { useRouter } from 'vue-router'
import { useToast } from '@/composables/useToast'

export interface Notification {
  id?: string
  type: 'check_in' | 'checkin' | 'credit_check_request' | 'credit_check_response'
  message: string
  user_id: string
  customer_id: number
  rooftop_id: string
  read?: boolean
  status?: string
  created_at?: string
  engaged_by?: string
}

export const useNotifications = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([])
  const userStore = useUserStore()
  const router = useRouter()
  const { showToast } = useToast()

  const unreadCount = computed(() => {
    return notifications.value.filter(n => !n.read).length
  })

  async function fetchNotifications() {
    try {
      const rooftopId = userStore.getRooftopId.value
      if (!rooftopId) {
        console.error('No rooftop ID available')
        return
      }

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('rooftop_id', rooftopId)
        .eq('dismissed', false)
        .order('created_at', { ascending: false })

      if (error) throw error
      notifications.value = data || []
      console.log('Fetched notifications:', notifications.value)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    }
  }

  async function setupNotificationsSubscription() {
    const rooftopId = userStore.getRooftopId.value
    if (!rooftopId) {
      console.error('No rooftop ID available for subscription')
      return
    }

    console.log('Setting up notifications subscription for rooftop:', rooftopId)
    
    return supabase
      .channel('public:notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `rooftop_id=eq.${rooftopId}`
        },
        handleRealTimeUpdate
      )
      .subscribe((status) => {
        console.log('Notification subscription status:', status)
      })
  }

  async function addNotification(notification: Omit<Notification, 'id' | 'read' | 'created_at'>) {
    try {
      const notificationData = {
        type: notification.type,
        message: notification.message,
        user_id: notification.user_id,
        customer_id: notification.customer_id,
        rooftop_id: notification.rooftop_id,
        status: notification.status || null,
        engaged_by: notification.engaged_by || null,
        read: false
      }

      const { error } = await supabase
        .from('notifications')
        .insert(notificationData)

      if (error) throw error
    } catch (error) {
      console.error('Error adding notification:', error)
      throw error
    }
  }

  async function markAsRead(notificationId: string) {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId)

      if (error) throw error

      const index = notifications.value.findIndex(n => n.id === notificationId)
      if (index !== -1) {
        notifications.value[index].read = true
      }
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  async function removeNotification(notificationId: string) {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ dismissed: true })
        .eq('id', notificationId)

      if (error) throw error

      notifications.value = notifications.value.filter(n => n.id !== notificationId)
    } catch (error) {
      console.error('Error removing notification:', error)
      throw error
    }
  }

  async function removeAllNotifications() {
    try {
      const rooftopId = userStore.getRooftopId.value
      if (!rooftopId) {
        console.error('No rooftop ID available')
        return
      }

      const { error } = await supabase
        .from('notifications')
        .update({ dismissed: true })
        .eq('rooftop_id', rooftopId)
        .eq('dismissed', false)

      if (error) throw error

      notifications.value = []
    } catch (error) {
      console.error('Error removing all notifications:', error)
      throw error
    }
  }

  async function handleRealTimeUpdate(payload: any) {
    const { new: newNotification, eventType } = payload
    console.log('Real-time notification update:', payload)

    if (newNotification) {
      if (eventType === 'INSERT') {
        notifications.value.unshift(newNotification)
      } else if (eventType === 'UPDATE') {
        const index = notifications.value.findIndex(n => n.id === newNotification.id)
        if (index !== -1) {
          notifications.value[index] = newNotification
        }
      } else if (eventType === 'DELETE') {
        notifications.value = notifications.value.filter(n => n.id !== newNotification.id)
      }

      if (eventType === 'INSERT') {
        await showToast({
          message: newNotification.message,
          color: getNotificationColor(newNotification),
          onClick: () => router.push('/tabs/tab1')
        })
      }
    }
  }

  function getNotificationColor(notification: Notification): 'success' | 'warning' | 'danger' | 'primary' {
    if (notification.type === 'check_in' || notification.type === 'checkin') return 'primary'
    if (notification.type === 'credit_check_request') return 'warning'
    if (notification.type === 'credit_check_response') {
      if (notification.status === 'approved') return 'success'
      if (notification.status === 'denied') return 'danger'
      return 'warning'
    }
    return 'primary'
  }

  return {
    notifications,
    unreadCount,
    fetchNotifications,
    setupNotificationsSubscription,
    addNotification,
    markAsRead,
    removeNotification,
    removeAllNotifications,
    handleRealTimeUpdate,
    getNotificationColor
  }
})
